import { useState } from 'react';
import { ActionIcon, Badge, Button, Group, Stack, Table, Text, Title } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import useProductKnowledgeArticles from './hooks/useProductKnowledgeArticles';
import useProductKnowledgeArticle from './hooks/useProductKnowledgeArticle';
import useDeleteProductKnowledgeArticle from './hooks/useDeleteProductKnowledgeArticle';
import ProductKnowledgeEditor from './ProductKnowledgeEditor';

type EditorState = { mode: 'list' } | { mode: 'create' } | { mode: 'edit'; articleId: string };

export default function ProductKnowledgeTab() {
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'list' });
  const { data: articles, isLoading, isError } = useProductKnowledgeArticles();
  const { mutateAsync: deleteArticle } = useDeleteProductKnowledgeArticle();

  const editingArticleId = editorState.mode === 'edit' ? editorState.articleId : null;
  const { data: editingArticle } = useProductKnowledgeArticle(editingArticleId);

  const handleDelete = async (articleId: string) => {
    try {
      await deleteArticle(articleId);
    } catch (error) {
      notifyAndLogError(
        'Failed to delete article',
        'An error occurred while deleting the article.',
        error,
      );
    }
  };

  if (editorState.mode === 'create') {
    return (
      <Stack gap="lg" p="lg">
        <Title order={3}>New article</Title>
        <ProductKnowledgeEditor article={null} onClose={() => setEditorState({ mode: 'list' })} />
      </Stack>
    );
  }

  if (editorState.mode === 'edit' && editingArticle) {
    return (
      <Stack gap="lg" p="lg">
        <Title order={3}>Edit article</Title>
        <ProductKnowledgeEditor
          article={editingArticle}
          onClose={() => setEditorState({ mode: 'list' })}
        />
      </Stack>
    );
  }

  if (isLoading || (editorState.mode === 'edit' && !editingArticle)) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return (
      <Text c="red" p="lg">
        Failed to load product knowledge articles.
      </Text>
    );
  }

  return (
    <Stack gap="lg" p="lg">
      <Group justify="space-between" align="center">
        <div>
          <Title order={3}>Product knowledge</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage what Siesta knows about the Afternoon platform. Each article becomes a file
            Siesta can read when the product knowledge skill is activated.
          </Text>
        </div>
        <Button size="xs" onClick={() => setEditorState({ mode: 'create' })}>
          Add article
        </Button>
      </Group>

      {!articles?.length ? (
        <Text c="dimmed" fs="italic">
          No articles yet. Add one to get started.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th w={80} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {articles.map(article => (
              <Table.Tr key={article.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Text fw={500}>{article.title}</Text>
                    {article.source === 'core' && (
                      <Badge size="xs" variant="light" color="gray">
                        Built-in
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {article.description}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {article.updatedAt ? new Date(article.updatedAt).toLocaleDateString() : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setEditorState({ mode: 'edit', articleId: article.id })}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                    {article.source !== 'core' && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleDelete(article.id)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
